import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ref, getDatabase, get, set, update } from "firebase/database";
import { toastrError, toastrSuccess } from "../components/toastr";
import { v4 } from "uuid";

// ─── Confirm popup ────────────────────────────────────────────────────────────
function ConfirmPopup({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
        <p className="text-sm font-bold text-gray-900 mb-1">Are you sure?</p>
        <p className="text-sm text-gray-500 mb-5">{message}</p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ enabled, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors ${
        enabled ? "bg-primary" : "bg-gray-300"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          enabled ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return Object.values(val);
}

// ─── Image helpers ────────────────────────────────────────────────────────────
function cropAndCompress(file, maxSize = 300, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = maxSize;
        canvas.height = maxSize;
        const ctx = canvas.getContext("2d");
        // Center-crop to square
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, maxSize, maxSize);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function MenuSettings() {
  const navigate = useNavigate();
  const email = localStorage.getItem("email");
  const [ownerId, setOwnerId] = useState(localStorage.getItem("ownerId") || "");
  const db = getDatabase();

  const [restaurantName, setRestaurantName] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [logoBase64, setLogoBase64] = useState("");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [newCatName, setNewCatName] = useState("");
  const [expanded, setExpanded] = useState({});
  const [newItem, setNewItem] = useState({});
  // Edit state
  const [editingCatId, setEditingCatId] = useState(null);
  const [editCatName, setEditCatName] = useState("");
  const [editingItem, setEditingItem] = useState(null); // { catId, itemId }
  const [editItemData, setEditItemData] = useState({});

  // Resolve ownerId from Firebase if it wasn't in localStorage; backfill if missing
  useEffect(() => {
    if (ownerId || !email) return;
    get(ref(db, "userInfo")).then((snap) => {
      if (!snap.exists()) return;
      const allData = snap.val();
      const key = Object.keys(allData).find((k) => allData[k].email === email);
      if (!key) return;
      if (allData[key].id) {
        localStorage.setItem("ownerId", allData[key].id);
        setOwnerId(allData[key].id);
      } else {
        const newId = v4();
        localStorage.setItem("ownerId", newId);
        setOwnerId(newId);
        update(ref(db, `userInfo/${key}`), { id: newId });
      }
    });
  }, [ownerId, email, db]);

  useEffect(() => {
    if (!ownerId) return;
    get(ref(db, `menu/${ownerId}`)).then((snap) => {
      if (!snap.exists()) return;
      const d = snap.val();
      setRestaurantName(d.restaurantName || "");
      setAnnouncement(d.announcement || "");
      setLogoBase64(d.logoBase64 || "");
      if (d.categories) {
        const cats = toArray(d.categories).map((cat) => ({
          ...cat,
          isEnabled: cat.isEnabled !== false,
          items: toArray(cat.items).map((item) => ({
            ...item,
            isEnabled: item.isEnabled !== false,
          })),
        }));
        setCategories(cats);
        const exp = {};
        cats.forEach((c) => { exp[c.id] = true; });
        setExpanded(exp);
      }
    });
  }, [db, ownerId]);

  async function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const base64 = await cropAndCompress(file);
      setLogoBase64(base64);
    } catch {
      toastrError("Could not process image");
    }
    // Reset input so same file can be re-selected
    e.target.value = "";
  }

  async function handleSave() {
    if (!ownerId) {
      toastrError("Session error — please log out and back in");
      return;
    }
    setSaving(true);
    try {
      const catsObj = {};
      for (const cat of categories) {
        const itemsObj = {};
        for (const item of cat.items) itemsObj[item.id] = item;
        catsObj[cat.id] = { ...cat, items: itemsObj };
      }
      await set(ref(db, `menu/${ownerId}`), {
        restaurantName,
        announcement,
        logoBase64,
        categories: catsObj,
      });
      toastrSuccess("Menu saved!");
    } catch (err) {
      toastrError("Could not save menu");
    }
    setSaving(false);
  }

  // ── Category actions ──────────────────────────────────────────────────────
  function addCategory() {
    if (!newCatName.trim()) return;
    const id = v4();
    setCategories((prev) => [
      ...prev,
      { id, name: newCatName.trim(), isEnabled: true, items: [] },
    ]);
    setExpanded((e) => ({ ...e, [id]: true }));
    setNewCatName("");
  }

  function toggleCategory(catId) {
    setCategories((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, isEnabled: !c.isEnabled } : c))
    );
  }

  function confirmRemoveCategory(cat) {
    setConfirm({
      message: `Remove "${cat.name}" and all its items?`,
      onConfirm: () => {
        setCategories((prev) =>
          prev.map((c) => (c.id === cat.id ? { ...c, isDeleted: true } : c))
        );
        setConfirm(null);
      },
    });
  }

  // ── Item actions ──────────────────────────────────────────────────────────
  function addItem(catId) {
    const item = newItem[catId] || {};
    if (!item.name?.trim()) return;
    const id = v4();
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              items: [
                ...c.items,
                {
                  id,
                  emoji: item.emoji?.trim() || "",
                  name: item.name.trim(),
                  price: item.price?.trim() || "",
                  isEnabled: true,
                },
              ],
            }
          : c
      )
    );
    setNewItem((prev) => ({
      ...prev,
      [catId]: { emoji: "", name: "", price: "" },
    }));
  }

  function toggleItem(catId, itemId) {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              items: c.items.map((i) =>
                i.id === itemId ? { ...i, isEnabled: !i.isEnabled } : i
              ),
            }
          : c
      )
    );
  }

  function confirmRemoveItem(cat, item) {
    setConfirm({
      message: `Remove "${item.name}" from ${cat.name}?`,
      onConfirm: () => {
        setCategories((prev) =>
          prev.map((c) =>
            c.id === cat.id
              ? {
                  ...c,
                  items: c.items.map((i) =>
                    i.id === item.id ? { ...i, isDeleted: true } : i
                  ),
                }
              : c
          )
        );
        setConfirm(null);
      },
    });
  }

  function setItemField(catId, field, value) {
    setNewItem((prev) => ({
      ...prev,
      [catId]: { ...prev[catId], [field]: value },
    }));
  }

  function saveEditCat(catId) {
    if (!editCatName.trim()) return;
    setCategories((prev) =>
      prev.map((c) => (c.id === catId ? { ...c, name: editCatName.trim() } : c))
    );
    setEditingCatId(null);
  }

  function saveEditItem(catId, itemId) {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === catId
          ? {
              ...c,
              items: c.items.map((i) =>
                i.id === itemId
                  ? {
                      ...i,
                      emoji: editItemData.emoji ?? i.emoji,
                      name: editItemData.name?.trim() || i.name,
                      price: editItemData.price ?? i.price,
                    }
                  : i
              ),
            }
          : c
      )
    );
    setEditingItem(null);
  }

  return (
    <div className="max-w-md mx-auto pb-10">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/settings")}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Menu Settings</h1>
          <p className="text-xs text-gray-500">Public menu shown to waiting customers</p>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-y-4">
        {/* Restaurant name & announcement */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5 flex flex-col gap-y-3">
          <h2 className="text-sm font-bold text-gray-900">Public Page Info</h2>

          {/* Logo upload */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-2 block">
              Business Logo{" "}
              <span className="text-gray-400">(shown on public page)</span>
            </label>
            <div className="flex items-center gap-4">
              <img
                src={logoBase64 || "logo.png"}
                alt="logo preview"
                className="w-16 h-16 rounded-xl object-cover border border-gray-200 flex-shrink-0"
              />
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="logo-upload"
                  className="inline-block cursor-pointer px-4 py-2 rounded-xl bg-primary-bg border border-primary/20 text-primary text-xs font-semibold"
                >
                  Upload Photo
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                {logoBase64 && (
                  <button
                    onClick={() => setLogoBase64("")}
                    className="text-xs text-red-400 font-medium text-left"
                  >
                    Remove photo
                  </button>
                )}
                <p className="text-xs text-gray-400">
                  Auto-cropped to square, compressed
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Business Display Name
            </label>
            <input
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="e.g. The Golden Fork"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Announcement{" "}
              <span className="text-gray-400">(optional — shown as a banner)</span>
            </label>
            <input
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="e.g. Kitchen closes at 10 PM tonight"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-1">Menu Categories</h2>
          <p className="text-xs text-gray-400 mb-4">
            e.g. Tiffin, Lunch, Burgers, Today's Special, Non-Veg
          </p>

          {/* Add category */}
          <div className="flex gap-2 mb-5">
            <input
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              className="flex-1 px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="Category name"
            />
            <button
              onClick={addCategory}
              disabled={!newCatName.trim()}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold ${
                newCatName.trim()
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              Add
            </button>
          </div>

          {categories.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No categories yet. Add one above.
            </p>
          )}

          {categories.filter((cat) => !cat.isDeleted).map((cat) => (
            <div
              key={cat.id}
              className={`border rounded-2xl mb-3 overflow-hidden transition-opacity ${
                cat.isEnabled ? "border-gray-200" : "border-gray-100 opacity-60"
              }`}
            >
              {/* Category row */}
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-50">
                {editingCatId === cat.id ? (
                  <>
                    <input
                      value={editCatName}
                      onChange={(e) => setEditCatName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveEditCat(cat.id);
                        if (e.key === "Escape") setEditingCatId(null);
                      }}
                      autoFocus
                      className="flex-1 px-3 py-1.5 bg-white rounded-lg border border-primary text-sm outline-none"
                    />
                    <button
                      onClick={() => saveEditCat(cat.id)}
                      className="text-xs font-semibold text-primary"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingCatId(null)}
                      className="text-xs font-semibold text-gray-400"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() =>
                        setExpanded((e) => ({ ...e, [cat.id]: !e[cat.id] }))
                      }
                      className="flex items-center gap-2 flex-1 text-left min-w-0"
                    >
                      <svg
                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                          expanded[cat.id] ? "rotate-90" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                      <span className="text-sm font-semibold text-gray-800 truncate">
                        {cat.name}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {cat.items.filter((i) => !i.isDeleted).length} items
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        setEditingCatId(cat.id);
                        setEditCatName(cat.name);
                      }}
                      className="text-xs text-gray-400 hover:text-primary flex-shrink-0"
                      title="Edit category name"
                    >
                      ✏️
                    </button>
                    <Toggle
                      enabled={cat.isEnabled}
                      onChange={() => toggleCategory(cat.id)}
                    />
                    <button
                      onClick={() => confirmRemoveCategory(cat)}
                      className="text-xs text-red-400 font-medium flex-shrink-0"
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>

              {/* Items list + add form */}
              {expanded[cat.id] && (
                <div className="px-4 pb-4 pt-2">
                  {cat.items.length === 0 && (
                    <p className="text-xs text-gray-400 py-2">
                      No items yet — add one below.
                    </p>
                  )}
                  {cat.items.filter((item) => !item.isDeleted).map((item) => (
                    <div
                      key={item.id}
                      className={`py-2.5 border-b border-gray-50 last:border-0 ${
                        !item.isEnabled ? "opacity-50" : ""
                      }`}
                    >
                      {editingItem?.catId === cat.id && editingItem?.itemId === item.id ? (
                        /* Edit mode */
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <div className="w-14">
                              <label className="text-xs text-gray-400 mb-1 block">Emoji</label>
                              <input
                                value={editItemData.emoji ?? item.emoji}
                                onChange={(e) =>
                                  setEditItemData((d) => ({ ...d, emoji: e.target.value }))
                                }
                                autoFocus
                                className="w-full px-2 py-1.5 bg-gray-50 rounded-lg border border-primary text-sm text-center outline-none"
                                placeholder="🍕"
                              />
                            </div>
                            <div className="flex-1">
                              <label className="text-xs text-gray-400 mb-1 block">Name *</label>
                              <input
                                value={editItemData.name ?? item.name}
                                onChange={(e) =>
                                  setEditItemData((d) => ({ ...d, name: e.target.value }))
                                }
                                className="w-full px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200 text-sm outline-none focus:border-primary"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-400 mb-1 block">Price</label>
                            <input
                              value={editItemData.price ?? item.price}
                              onChange={(e) =>
                                setEditItemData((d) => ({ ...d, price: e.target.value }))
                              }
                              className="w-full px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200 text-sm outline-none focus:border-primary"
                              placeholder="e.g. ₹40"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveEditItem(cat.id, item.id)}
                              className="flex-1 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingItem(null)}
                              className="flex-1 py-1.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Display mode */
                        <div className="flex items-center gap-3">
                          {item.emoji && (
                            <span className="text-xl flex-shrink-0">{item.emoji}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {item.name}
                            </p>
                            {item.price && (
                              <p className="text-xs text-primary font-semibold">
                                {item.price}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={() => {
                                setEditingItem({ catId: cat.id, itemId: item.id });
                                setEditItemData({});
                              }}
                              className="text-xs text-gray-400 hover:text-primary"
                              title="Edit item"
                            >
                              ✏️
                            </button>
                            <Toggle
                              enabled={item.isEnabled}
                              onChange={() => toggleItem(cat.id, item.id)}
                            />
                            <button
                              onClick={() => confirmRemoveItem(cat, item)}
                              className="text-xs text-red-400 font-medium"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add item form */}
                  <div className="mt-3 bg-gray-50 rounded-xl p-3 flex flex-col gap-2">
                    <div className="flex gap-2">
                      <div className="w-14">
                        <label className="text-xs text-gray-400 mb-1 block">
                          Emoji
                        </label>
                        <input
                          value={newItem[cat.id]?.emoji || ""}
                          onChange={(e) =>
                            setItemField(cat.id, "emoji", e.target.value)
                          }
                          className="w-full px-2 py-2 bg-white rounded-lg border border-gray-200 text-sm text-center outline-none"
                          placeholder="🍕"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs text-gray-400 mb-1 block">
                          Item name *
                        </label>
                        <input
                          value={newItem[cat.id]?.name || ""}
                          onChange={(e) =>
                            setItemField(cat.id, "name", e.target.value)
                          }
                          onKeyDown={(e) =>
                            e.key === "Enter" && addItem(cat.id)
                          }
                          className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm outline-none focus:border-primary"
                          placeholder="e.g. Idly"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 mb-1 block">
                        Price{" "}
                        <span className="text-gray-300">(optional)</span>
                      </label>
                      <input
                        value={newItem[cat.id]?.price || ""}
                        onChange={(e) =>
                          setItemField(cat.id, "price", e.target.value)
                        }
                        className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm outline-none focus:border-primary"
                        placeholder="e.g. ₹40 or $5"
                      />
                    </div>
                    <button
                      onClick={() => addItem(cat.id)}
                      disabled={!newItem[cat.id]?.name?.trim()}
                      className={`w-full py-2 rounded-lg text-sm font-semibold ${
                        newItem[cat.id]?.name?.trim()
                          ? "bg-primary text-white"
                          : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      + Add Item
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 rounded-xl font-semibold text-sm bg-primary text-white disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Menu"}
        </button>
      </div>

      {confirm && (
        <ConfirmPopup
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

export default MenuSettings;
